import React, { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { useAuthStore, useLanguageStore } from '@/frontend/src/store';
import { useTranslation } from '@/frontend/src/i18n/translations';

/**
 * OrderStatusTracker — трекер статуса заказов в производстве (ТЗ 4.4).
 */
const API = process.env.REACT_APP_BACKEND_URL;

export const OrderStatusTracker = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const token = useAuthStore((s) => s.token);
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const l = t.orderTracker || {};

  const STAGES = [
    { key: 'received', label: l.received || 'Order received' },
    { key: 'design', label: l.design || 'Engineering / Drawings' },
    { key: 'production', label: l.production || 'In production' },
    { key: 'qc_packing', label: l.qc_packing || 'QC & Packing' },
    { key: 'ready', label: l.ready || 'Ready for installation' },
  ];

  const stageIndex = (status) => {
    const i = STAGES.findIndex((s) => s.key === status);
    return i < 0 ? 0 : i;
  };

  const StatusBar = ({ status }) => {
    const current = stageIndex(status);
    return (
      <div className="flex items-center" data-testid="order-status-bar">
        {STAGES.map((s, i) => {
          const done = i < current;
          const isCurrent = i === current;
          return (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center text-center shrink-0" style={{ width: 96 }}>
                <div
                  className={`w-7 h-7 flex items-center justify-center border text-[11px] transition-luxe ${
                    done || isCurrent ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#9E9E9E] border-[#ECECEC]'
                  }`}
                >
                  {done ? <Check className="w-3.5 h-3.5" strokeWidth={2} /> : i + 1}
                </div>
                <span className={`mt-2 text-[9px] leading-tight tracking-[0.08em] uppercase ${isCurrent ? 'text-[#0A0A0A] font-medium' : 'text-[#9E9E9E]'}`}>
                  {s.label}
                </span>
              </div>
              {i < STAGES.length - 1 && (
                <div className={`h-px flex-1 -mt-5 transition-luxe ${i < current ? 'bg-[#0A0A0A]' : 'bg-[#ECECEC]'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/b2b/orders`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          if (alive) setOrders(data.orders || (Array.isArray(data) ? data : []));
        }
      } catch (e) {
        /* ignore */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  return (
    <div data-testid="b2b-order-tracker">
      <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E9E9E] mb-2 font-medium">
        {l.eyebrow || 'Production · Live status'}
      </p>
      <h2 className="text-3xl font-extralight tracking-tight text-[#0A0A0A] mb-8">
        {l.title || 'Order Status'}
      </h2>

      {loading ? (
        <p className="text-sm font-light text-[#4A4A4A]">{l.loading || 'Loading orders…'}</p>
      ) : orders.length === 0 ? (
        <div className="border border-[#ECECEC] bg-white p-8" data-testid="orders-empty">
          <p className="text-sm font-light text-[#4A4A4A] mb-8 max-w-lg leading-relaxed">
            {l.empty || 'You have no orders in production yet.'}
          </p>
          <div className="overflow-x-auto pb-2">
            <div className="min-w-[560px] opacity-60">
              <StatusBar status="received" />
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((o) => (
            <div key={o.id} className="border border-[#ECECEC] bg-white p-6" data-testid={`order-${o.id}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-6">
                <h3 className="font-medium text-[#0A0A0A]">{o.project_name || o.reference || `Order ${o.id}`}</h3>
                {o.reference && <span className="text-[11px] text-[#9E9E9E] font-mono tracking-wide">{o.reference}</span>}
              </div>
              <div className="overflow-x-auto pb-2">
                <div className="min-w-[560px]">
                  <StatusBar status={o.status} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrderStatusTracker;